// <pos-order-list/> — 訂單管理（owner / cashier 視圖）
// Status filter + card flow + 改狀態按鈕 + 手動 refresh
// see src/designs/owner-orders.html + architecture.md#L510

import { listOrders, updateOrderStatus, deleteOrder, printOrder, listNotificationRoutes } from '../lib/api.js';
import { allowedTransitionsForRole, isTransitionAllowedForPayment } from '../lib/state-machine/order.js';
import { getSupabase } from '../lib/supabase-client.js';
import { getStationRole, getStationName, STATION_LABELS, shouldNotify } from '../lib/notification-routes.js';
import {
  getWorkflow,
  allowedTransitionsFromWorkflow,
  getStateDisplay,
  WORKFLOW_EVENT_UPDATED,
} from '../lib/workflow-cache.js';

// Fallback labels · workflow cache 尚未載入時用（cold mount / offline）
// 正常情況 label / color 從 workflow.states 動態取
const STATUS_LABELS_FALLBACK = {
  pending: '新訂單',
  paid: '付款完成待準備',
  preparing: '準備中',
  ready: '完成待出',
  completed: '已完成',
  cancelled: '已取消',
  refunded: '已退款',
};
const TRANSITION_LABELS = {
  paid: '✓ 已收款',
  preparing: '▶ 開始準備',
  ready: '✓ 完成準備',
  completed: '✓ 結單',
  cancelled: '✕ 取消',
  refunded: '↩ 退款',
};
const FALLBACK_CARD_COLORS = {
  pending: '#EF4444', paid: '#10B981', preparing: '#3B82F6',
  ready: '#8B5CF6', completed: '#6B7280',
  cancelled: '#EF4444', refunded: '#6B7280',
};

// Fallback · workflow 未載入時用
const FILTER_GROUPS_FALLBACK = [
  { key: 'pending',    label: '新訂單',         color: '#EF4444' },
  { key: 'paid',       label: '付款完成待準備', color: '#10B981' },
  { key: 'preparing',  label: '準備中',         color: '#3B82F6' },
  { key: 'ready',      label: '完成待出',       color: '#8B5CF6' },
  { key: 'completed',  label: '已完成',         color: '#6B7280' },
  { key: 'cancelled',  label: '已取消',         color: '#EF4444' },
  { key: 'all',        label: '全部（當天）',   color: '#A78BFA' },
];

/**
 * 依 workflow 動態產生 filter tabs
 * 包含：workflow 所有 state（照 sort_order）+ 尾巴加「全部（當天）」
 * 若 workflow 尚未載入，回 fallback
 */
function buildFilterGroups(workflow) {
  if (!workflow?.states?.length) return FILTER_GROUPS_FALLBACK;
  const sorted = [...workflow.states].sort((a, b) => a.sort_order - b.sort_order);
  // QA 2026-07-07 付費條件 #4：七個 tab 讓老闆眼花 → 預設收斂成兩個主 tab，
  // 其餘收進「更多 ▾」。用 workflow flags 分組（tenant 可自訂 workflow，不硬編狀態名）：
  //   進行中 = 所有非終態 state 合併；主 tab 2 = 完成終態（is_terminal_success）
  //   更多 = 各別進行中細分 + 取消/退款終態 + 全部
  const active = sorted.filter(s => !s.is_terminal_success && !s.is_terminal_cancel);
  const success = sorted.filter(s => s.is_terminal_success);
  const cancel = sorted.filter(s => s.is_terminal_cancel);
  return [
    { key: 'active', label: '進行中', color: '#3B82F6', activeCodes: active.map(s => s.code) },
    ...success.map(s => ({ key: s.code, label: s.label, color: s.color })),
    { key: '_more', label: '更多 ▾', color: '#6B7280', more: [
      ...active.map(s => ({ key: s.code, label: s.label, color: s.color })),
      ...cancel.map(s => ({ key: s.code, label: s.label, color: s.color })),
      { key: 'all', label: '全部（當天）', color: '#A78BFA' },
    ] },
  ];
}

/** 把 #RRGGBB 轉成 rgba(r,g,b,a)，用於漸層背景 */
function hexToRgba(hex, alpha) {
  const m = /^#([0-9a-f]{6})$/i.exec(hex || '#6B7280');
  if (!m) return `rgba(107,114,128,${alpha})`;
  const n = parseInt(m[1], 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`;
}

function escapeHtml(s) {
  return String(s ?? '').replace(/[&<>"']/g, c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c]));
}

function timeAgo(iso) {
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return '剛剛';
  if (min < 60) return `${min} 分鐘前`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} 小時前`;
  return new Date(iso).toLocaleDateString('zh-TW');
}

function hhmm(iso) {
  return new Date(iso).toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit', hour12: false });
}

class PosOrderList extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.state = {
      orders: [],
      counts: {},
      filter: 'active',
      moreOpen: false,
      query: '',
      loading: true,
      error: null,
      lastUpdate: null,
      highlightIds: new Set(),
      realtimeStatus: 'connecting', // 'connecting' | 'live' | 'offline'
      stationRole: getStationRole(),
      stationName: getStationName(),
      routes: [],
    };
    this._channel = null;
    this._highlightTimers = new Map();
    this._pollTimer = null;
    // Phase 2c · workflow cache（null = 還沒載入 → 用 hardcode fallback）
    this._workflow = null;
    this._activeCodes = ['pending', 'paid', 'preparing', 'ready'];  // workflow 載入前 fallback
    this._onWorkflowUpdated = (e) => {
      this._workflow = e.detail;
      this._syncActiveCodes();
      this.render();
    };
    // Codex PR19-I1：advance 期間對同單 disable 按鈕防雙擊（解「完成準備彈錯但實際成功」—
    // realtime reload + 再點擊撞 P0001 ready→ready）
    this._inFlightIds = new Set();
    // 手動出單：出單中 / 剛出單成功（✓ 標記 2.5s 後回復）的訂單 id
    this._printingIds = new Set();
    this._printedIds = new Set();
    this._printedTimers = new Map();
    // Codex PR19-I2：防快速 mount/unmount race — subscribeRealtime 是 async，
    // connectedCallback 兩次相繼觸發時，舊實例的 async subscribe 仍可能在 teardown 後完成
    this._mounted = false;
  }

  /** 依 workflow flags 計算「進行中」合併視圖的 state codes（QA #4 tab 收斂） */
  _syncActiveCodes() {
    const states = this._workflow?.states || [];
    this._activeCodes = states
      .filter(st => !st.is_terminal_success && !st.is_terminal_cancel)
      .map(st => st.code);
    if (!this._activeCodes.length) this._activeCodes = ['pending', 'paid', 'preparing', 'ready'];
  }

  async connectedCallback() {
    this._mounted = true;
    this.render();
    // Phase 2c · 聽 workflow 更新（background refresh 到新版本時觸發）
    window.addEventListener(WORKFLOW_EVENT_UPDATED, this._onWorkflowUpdated);
    // 效能 #2 · 平行 fetch（orders + routes + workflow）
    const tenantId = document.body.dataset.tenantId;
    const [, , workflow] = await Promise.all([
      this.reload(),
      this.loadRoutes(),
      tenantId ? getWorkflow(tenantId).catch(() => null) : Promise.resolve(null),
    ]);
    if (!this._mounted) return;
    if (workflow) {
      this._workflow = workflow;
      this._syncActiveCodes();
      this.render();
    }
    if (!this._mounted) return;  // 已 unmount 就不 subscribe
    // 效能 #4 · Realtime 延遲初始化 — WebSocket handshake + auth 要 200-500ms，
    // 先讓 critical render path 跑完再建 channel。1 秒內訂單變更遺漏機率極低
    // （reload 已撈最新）；若 disconnect 會清掉 timer。
    this._realtimeTimer = setTimeout(() => {
      if (!this._mounted) return;
      this.subscribeRealtime();
    }, 800);
  }

  async loadRoutes() {
    try {
      const { routes } = await listNotificationRoutes();
      this.state.routes = routes || [];
    } catch (err) {
      console.warn('[order-list] loadRoutes failed, using defaults', err);
      this.state.routes = [];
    }
  }

  async disconnectedCallback() {
    this._mounted = false;
    // Phase 2c · 解 workflow listener
    window.removeEventListener(WORKFLOW_EVENT_UPDATED, this._onWorkflowUpdated);
    // 效能 #4 · 清掉可能還沒 fire 的 realtime timer
    if (this._realtimeTimer) {
      clearTimeout(this._realtimeTimer);
      this._realtimeTimer = null;
    }
    this.teardownRealtime();
    this._highlightTimers.forEach(t => clearTimeout(t));
    this._highlightTimers.clear();
    this._printedTimers.forEach(t => clearTimeout(t));
    this._printedTimers.clear();
    if (this._audioCtx && this._audioCtx.state !== 'closed') {
      try { await this._audioCtx.close(); } catch (_) { /* noop */ }
    }
    this._audioCtx = null;
  }

  subscribeRealtime() {
    // Codex PR19-I2：快速 mount/unmount race — 若已 unmount 直接跳出
    if (!this._mounted) return;
    const supabase = getSupabase();
    if (!supabase?.channel) return; // dev fallback client 沒 realtime
    // P1 fix 2026-04-23：原本 channel name 固定 'pos-orders'，component 第二次 mount
    // （例如 navigate 離開再回來、HMR）時 supabase client 內部重用同一 channel 實例
    // （已 subscribe），再 `.on()` 會拋 "cannot add postgres_changes callbacks after subscribe"。
    // 改用 uniq instance name + 明確 teardown 前序保險。
    if (this._channel) {
      try { supabase.removeChannel(this._channel); } catch (_) { /* noop */ }
      this._channel = null;
    }
    const channelName = `pos-orders-${Math.random().toString(36).slice(2, 10)}`;
    // RLS 會自動過濾跨 tenant，channel filter 不需要額外 tenant_id
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'orders' },
        (payload) => this.handleInsert(payload)
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'orders' },
        (payload) => this.handleUpdate(payload)
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'notification_routes' },
        () => this.loadRoutes().then(() => this.render())
      )
      // 訂 order_items INSERT：後端先 insert orders、再 insert order_items（兩次獨立 commit）
      // 若前端只聽 orders，reload 時很可能 order_items 還沒寫進去 → 卡片顯示「無品項」
      // 這裡補訂閱，收到 items INSERT 再 silent reload 一次，4 秒內自動補齊
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'order_items' },
        () => this.scheduleSilentReload()
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          this.state.realtimeStatus = 'live';
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
          this.state.realtimeStatus = 'offline';
        }
        this.render();
      });
    this._channel = channel;
  }

  // Silent reload with in-flight guard：同時段多個 realtime event 只會觸發一次 reload
  // 避免 UPDATE 爆量（例如批次改狀態）時並行請求互相覆蓋 state
  scheduleSilentReload() {
    if (this._reloadPromise) return this._reloadPromise;
    this._reloadPromise = this.reload({ silent: true }).finally(() => {
      this._reloadPromise = null;
    });
    return this._reloadPromise;
  }

  teardownRealtime() {
    if (this._channel) {
      try { getSupabase().removeChannel(this._channel); } catch (_) { /* noop */ }
      this._channel = null;
    }
  }

  async handleInsert(payload) {
    const newRow = payload?.new;
    if (!newRow?.id) return;

    // 先拉最新訂單（含 items）進 state
    const already = this.state.orders.some(o => o.id === newRow.id);
    await this.scheduleSilentReload();

    // 店員自己在 POS 建單（customer_source='staff'）通常不該擾自己，
    // 但不同裝置之間仍要通知（例：員工 A 的櫃檯建單，員工 B 的廚房裝置要知道）
    // 新訂單進入 pending → 走 notification_routes 決定此裝置是否響
    const status = newRow.status || 'pending';
    const { notify, sound, flash } = shouldNotify(status, this.state.routes, this.state.stationRole);
    if (!notify || already) return;

    if (flash) this.flashHighlight(newRow.id);
    if (sound) this.playNewOrderSound();
  }

  // UPDATE 事件：若 status 變化 → 看此裝置是否要閃+響
  async handleUpdate(payload) {
    const oldRow = payload?.old || {};
    const newRow = payload?.new;
    if (!newRow?.id) return;

    // 排好 reload（含品項等最新資訊）
    this.scheduleSilentReload();

    // 狀態沒變 → 沒事（只是改備註等）
    if (oldRow.status && oldRow.status === newRow.status) return;
    const { notify, sound, flash } = shouldNotify(newRow.status, this.state.routes, this.state.stationRole);
    if (!notify) return;

    if (flash) this.flashHighlight(newRow.id);
    if (sound) this.playNewOrderSound();
  }

  flashHighlight(orderId) {
    this.state.highlightIds.add(orderId);
    this.render();
    const prev = this._highlightTimers.get(orderId);
    if (prev) clearTimeout(prev);
    const timer = setTimeout(() => {
      this.state.highlightIds.delete(orderId);
      this._highlightTimers.delete(orderId);
      this.render();
    }, 4000);
    this._highlightTimers.set(orderId, timer);
  }

  playNewOrderSound() {
    // WebAudio 單聲 beep（無需音檔），失敗靜默（例：使用者還沒互動瀏覽器會擋）
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = this._audioCtx || (this._audioCtx = new Ctx());
      if (ctx.state === 'suspended') ctx.resume();
      const now = ctx.currentTime;
      // 兩聲「叮—咚」
      [
        { freq: 880, start: 0,    dur: 0.18 },
        { freq: 660, start: 0.22, dur: 0.22 },
      ].forEach(({ freq, start, dur }) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0.0001, now + start);
        gain.gain.exponentialRampToValueAtTime(0.25, now + start + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + start + dur);
        osc.connect(gain).connect(ctx.destination);
        osc.start(now + start);
        osc.stop(now + start + dur + 0.05);
      });
    } catch (_) {
      /* 靜默失敗 */
    }
  }

  async reload(opts = {}) {
    const silent = opts.silent === true;
    if (!silent) {
      this.state.loading = true;
      this.state.error = null;
      this.render();
    }
    try {
      // Fetch today's orders (scope=today) + client-side group-by-status for counts.
      // 「全部（當天）」filter 只顯示當天；其他狀態 filter 也只看當天（避免歷史訂單洗版）。
      const data = await listOrders({ limit: 200, scope: 'today' });
      const orders = data.orders || [];
      const counts = orders.reduce((m, o) => { m[o.status] = (m[o.status] || 0) + 1; return m; }, {});
      counts.all = orders.length;
      this.state.orders = orders;
      this.state.counts = counts;
      this.state.lastUpdate = new Date();
    } catch (err) {
      this.state.error = err.message_zh || err.message || '讀取訂單失敗';
    } finally {
      this.state.loading = false;
      this.render();
    }
  }

  async advance(orderId, toStatus) {
    // Codex PR19-I1：防雙擊 / realtime reload 後重點。若此單正在飛行就忽略。
    if (this._inFlightIds.has(orderId)) return;
    this._inFlightIds.add(orderId);
    this.render();  // 重繪讓按鈕失效
    try {
      await updateOrderStatus(orderId, toStatus);
      await this.reload();
    } catch (err) {
      alert(`✗ ${err.message_zh || err.message}`);
    } finally {
      this._inFlightIds.delete(orderId);
      // reload 已 render 過一次；若是 error 路徑 reload 沒跑，這裡補 render 解除 disabled
      if (this._mounted) this.render();
    }
  }

  // 手動出單：把收據排入 printer_jobs（CloudPRNT / Bridge 兩軌都會取件）
  async printOrder(orderId) {
    if (this._printingIds.has(orderId)) return;
    this._printingIds.add(orderId);
    this.render();
    try {
      const { queued, reason } = await printOrder(orderId);
      if (!queued) {
        alert(reason === 'no_active_printer'
          ? '✗ 尚未設定出單機，請先完成印表機連線（設定 → 印表機）'
          : `✗ 出單失敗（${reason || '未知原因'}）`);
        return;
      }
      // 成功：按鈕短暫顯示 ✓，不用彈窗打斷
      this._printedIds.add(orderId);
      const prev = this._printedTimers.get(orderId);
      if (prev) clearTimeout(prev);
      this._printedTimers.set(orderId, setTimeout(() => {
        this._printedIds.delete(orderId);
        this._printedTimers.delete(orderId);
        if (this._mounted) this.render();
      }, 2500));
    } catch (err) {
      alert(`✗ ${err.message_zh || err.message || '出單失敗'}`);
    } finally {
      this._printingIds.delete(orderId);
      if (this._mounted) this.render();
    }
  }

  async deleteOrder(orderId, orderNumber) {
    if (!confirm(`確定刪除訂單 #${orderNumber}？此動作無法復原。`)) return;
    try {
      await deleteOrder(orderId);
      await this.reload();
    } catch (err) {
      alert(`✗ ${err.message_zh || err.message}`);
    }
  }

  currentFilterOrders() {
    let list;
    if (this.state.filter === 'all') list = this.state.orders;
    else if (this.state.filter === 'active') {
      const codes = new Set(this._activeCodes || []);
      list = this.state.orders.filter(o => codes.has(o.status));
    } else list = this.state.orders.filter(o => o.status === this.state.filter);
    // QA 建議 #2：桌號 / 單號快速篩選（客人問「我剛那桌」立刻找到）
    const q = (this.state.query || '').trim().toLowerCase();
    if (q) {
      list = list.filter(o =>
        String(o.table_number || '').toLowerCase().includes(q)
        || String(o.order_number || '').toLowerCase().includes(q));
    }
    return list;
  }

  render() {
    const { loading, error, counts, filter, lastUpdate } = this.state;
    const role = document.body.dataset.role || 'cashier';
    const filtered = this.currentFilterOrders();
    // Phase 2c 收尾 · 動態從 workflow 產生 filter groups + label
    const filterGroups = buildFilterGroups(this._workflow);
    const statusLabel = (code) => {
      if (code === 'all') return '所有訂單';
      if (this._workflow) {
        const d = getStateDisplay(this._workflow, code);
        return d.label;
      }
      return STATUS_LABELS_FALLBACK[code] || code;
    };

    this.shadowRoot.innerHTML = `
      <style>${this.styles()}</style>

      <main>
        <aside class="filters">
          <div class="filters__title">狀態</div>
          ${filterGroups.map(g => {
            if (g.more) {
              const inMore = g.more.some(m => m.key === filter);
              const open = this.state.moreOpen || inMore;
              return `
                <div class="filter-row filter-row--more" data-act="toggle-more">
                  <span>${open ? '收合 ▴' : escapeHtml(g.label)}</span>
                </div>
                ${open ? g.more.map(m => `
                  <div class="filter-row filter-row--sub ${filter === m.key ? 'is-active' : ''}" data-key="${m.key}">
                    <span><span class="status-dot" style="background:${m.color}"></span>${escapeHtml(m.label)}</span>
                    <span class="filter-row__count">${m.key === 'all' ? (this.state.orders?.length || 0) : (counts[m.key] || 0)}</span>
                  </div>`).join('') : ''}`;
            }
            const count = g.key === 'active'
              ? (g.activeCodes || []).reduce((sum, c) => sum + (counts[c] || 0), 0)
              : (counts[g.key] || 0);
            return `
            <div class="filter-row ${filter === g.key ? 'is-active' : ''}" data-key="${g.key}">
              <span><span class="status-dot" style="background:${g.color}"></span>${escapeHtml(g.label)}</span>
              <span class="filter-row__count">${count}</span>
            </div>`;
          }).join('')}
        </aside>

        <section class="orders">
          <div class="orders__head">
            <h1 class="orders__title">${escapeHtml(filter === 'active' ? '進行中' : statusLabel(filter))}</h1>
            <div class="orders__head__right">
              <a class="station-badge station-badge--${this.state.stationRole}" href="#/settings/stations" title="點此修改此裝置的站點角色">
                <span class="station-badge__icon">🔔</span>
                ${STATION_LABELS[this.state.stationRole] || this.state.stationRole}${this.state.stationName ? ` · ${escapeHtml(this.state.stationName)}` : ''}
              </a>
              ${this.state.realtimeStatus === 'offline' ? `
              <span class="rt-badge rt-badge--offline" title="即時通知斷線，畫面可能不是最新">
                <span class="rt-dot"></span>離線
              </span>` : ''}
              <input class="orders__search" type="search" placeholder="🔍 桌號 / 單號"
                     value="${escapeHtml(this.state.query)}" data-act="search" />
              <button class="orders__refresh" data-act="refresh">
                <span>↻ 重新整理</span>
                <span class="orders__refresh__time">${lastUpdate ? hhmm(lastUpdate) : '—'}</span>
              </button>
            </div>
          </div>

          ${error ? `<div class="banner err">✗ ${escapeHtml(error)}</div>` : ''}
          ${loading ? `<div class="banner loading">載入中…</div>` : ''}

          ${!loading && filtered.length === 0 ? `
            <div class="empty">
              <div class="empty__icon">📭</div>
              <div class="empty__text">目前沒有${escapeHtml(filter === 'all' ? '' : filter === 'active' ? '進行中' : statusLabel(filter))}訂單</div>
            </div>
          ` : filtered.map(o => this.renderCard(o, role)).join('')}
        </section>
      </main>
    `;

    this.wireEvents();
  }

  renderCard(o, role) {
    // Phase 2c 收尾 · 卡片 label / color 從 workflow 動態取
    // 沒 workflow cache 時 fallback 到硬編 mapping
    let stateLabel, stateColor;
    if (this._workflow) {
      const d = getStateDisplay(this._workflow, o.status);
      stateLabel = d.label;
      stateColor = d.color;
    } else {
      stateLabel = STATUS_LABELS_FALLBACK[o.status] || o.status;
      stateColor = FALLBACK_CARD_COLORS[o.status] || '#6B7280';
    }
    const cardStyle = `border-left:4px solid ${stateColor}; background: linear-gradient(90deg, ${hexToRgba(stateColor, 0.08)}, var(--bg-elevated) 20%);`;

    // Phase 2c · 若 workflow cache 已載入，用 dynamic transitions（含 label / role）；
    // 否則 fallback 到 hardcoded state-machine.js。兩者都過濾掉 refunded（owner 走另外退款流程）
    let actions;
    if (this._workflow) {
      actions = allowedTransitionsFromWorkflow(this._workflow, o.status, role)
        .filter(t => t.to_code !== 'refunded')
        .map(t => ({ to: t.to_code, label: t.label }));
    } else {
      actions = allowedTransitionsForRole(o.status, role)
        .filter(s => s !== 'refunded')
        .map(s => ({ to: s, label: TRANSITION_LABELS[s] || s }));
    }
    // B2（非現金未付款不得出餐）：隱藏跳過付款的前進動作（dynamic / static 兩路徑共用）。
    //   後端 change_order_status 為最終守門；此處讓 UI 一致，避免店員按了才吃 P0001 錯誤。
    actions = actions.filter(a => isTransitionAllowedForPayment(a.to, o.status, o.payment_method));
    const canDelete = role === 'owner' && (o.status === 'cancelled' || o.status === 'refunded');
    const inFlight = this._inFlightIds?.has(o.id);

    const items = (o.items || []).map(i => `
      <div class="order-card__item">
        <span>${escapeHtml(i.menu_item_name)}</span>
        <span class="order-card__item__qty">× ${i.qty}</span>
      </div>
    `).join('');

    const flash = this.state.highlightIds.has(o.id) ? 'order-card--flash' : '';

    return `
      <article class="order-card ${flash}" style="${cardStyle}">
        <div class="order-card__meta">
          <div class="order-card__id">#${escapeHtml(o.order_number || o.id.slice(0, 8))}</div>
          <div class="order-card__table">桌號 <strong>${escapeHtml(o.table_number || '-')}</strong></div>
          <div class="order-card__time">${timeAgo(o.created_at)} · ${escapeHtml(stateLabel)}</div>
          ${o.customer_source === 'public_qr' ? '<div class="order-card__badge">📱 QR 點餐</div>' : ''}
        </div>
        <div>
          <div class="order-card__items">${items || '<div class="order-card__item"><span>（無品項）</span></div>'}</div>
          ${o.customer_note ? `<div class="order-card__note">備註：${escapeHtml(o.customer_note)}</div>` : ''}
        </div>
        <div class="order-card__right">
          <div class="order-card__total">NT$ ${o.total || 0}</div>
          <div class="order-card__actions">
            <button class="btn-step btn-step--print" data-act="print" data-id="${o.id}" ${this._printingIds.has(o.id) ? 'disabled' : ''}>
              ${this._printingIds.has(o.id) ? '出單中…' : this._printedIds.has(o.id) ? '✓ 已送出' : '🖨 出單'}
            </button>
            ${actions.map(a => `
              <button class="btn-step ${a.to !== 'cancelled' ? 'btn-step--primary' : ''}" data-act="advance" data-id="${o.id}" data-to="${a.to}" ${inFlight ? 'disabled' : ''}>
                ${inFlight ? '處理中…' : escapeHtml(a.label)}
              </button>
            `).join('')}
            ${canDelete ? `
              <button class="btn-step btn-step--danger" data-act="delete" data-id="${o.id}" data-num="${escapeHtml(o.order_number || o.id.slice(0, 8))}">
                🗑 刪除
              </button>
            ` : ''}
          </div>
        </div>
      </article>
    `;
  }

  wireEvents() {
    const root = this.shadowRoot;
    const search = root.querySelector('[data-act="search"]');
    if (search) {
      search.addEventListener('input', () => {
        this.state.query = search.value;
        clearTimeout(this._searchTimer);
        this._searchTimer = setTimeout(() => this.render(), 350);   // 防抖後重畫
      });
    }
    root.querySelectorAll('.filter-row').forEach(row => {
      row.addEventListener('click', () => {
        if (row.dataset.act === 'toggle-more') {
          this.state.moreOpen = !this.state.moreOpen;
          this.render();
          return;
        }
        this.state.filter = row.dataset.key;
        this.render();
      });
    });
    root.querySelectorAll('[data-act]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const act = btn.dataset.act;
        if (act === 'refresh') this.reload();
        else if (act === 'advance') {
          const id = btn.dataset.id;
          const to = btn.dataset.to;
          if (to === 'cancelled' && !confirm('確定取消這張訂單嗎？')) return;
          this.advance(id, to);
        }
        else if (act === 'print') {
          this.printOrder(btn.dataset.id);
        }
        else if (act === 'delete') {
          this.deleteOrder(btn.dataset.id, btn.dataset.num);
        }
      });
    });
  }

  styles() {
    return `
      :host { display: block; font-family: var(--font-sans); color: var(--fg-base); background: var(--bg-base); min-height: calc(100vh - 60px); }

      main { display: grid; grid-template-columns: 240px minmax(0, 1fr); gap: var(--s-6); padding: var(--s-6) var(--s-8); }

      .filters { display: grid; gap: var(--s-2); align-content: start; }
      .filters__title { font-size: .75rem; text-transform: uppercase; letter-spacing: 2px; color: var(--fg-dim); padding: 0 var(--s-3) var(--s-2); }
      .filter-row {
        display: flex; align-items: center; justify-content: space-between;
        padding: var(--s-3) var(--s-4);
        background: var(--bg-elevated); border: 1px solid var(--border); border-radius: var(--r-md);
        cursor: pointer; font-size: .95rem;
      }
      .filter-row:hover { border-color: var(--border-strong); }
      .filter-row.is-active { border-color: var(--brand-primary); background: rgba(232, 88, 12, 0.08); }
      .status-dot { width: 10px; height: 10px; border-radius: var(--r-full); margin-right: var(--s-2); display: inline-block; vertical-align: middle; }
      .status-dot--new    { background: var(--danger); box-shadow: 0 0 8px var(--danger); }
      .status-dot--paid   { background: var(--success); box-shadow: 0 0 8px var(--success); }
      .status-dot--prep   { background: var(--warning); box-shadow: 0 0 8px var(--warning); }
      .status-dot--ready  { background: var(--brand-accent); box-shadow: 0 0 8px var(--brand-accent); }
      .status-dot--done   { background: var(--fg-dim); }
      .status-dot--cancel { background: var(--fg-muted); opacity: .6; }
      .status-dot--all    { background: var(--brand-primary); }
      .filter-row--sub { padding-left: var(--s-6); font-size: .88rem; opacity: .9; }
      .filter-row--more { color: var(--fg-dim); font-size: .85rem; }
      .filter-row__count { color: var(--fg-muted); font-family: var(--font-mono); font-size: .85rem; }

      .orders { display: grid; gap: var(--s-4); }
      .orders__head { display: flex; justify-content: space-between; align-items: center; margin-bottom: var(--s-3); flex-wrap: wrap; gap: var(--s-3); }
      .orders__head__right { display: flex; align-items: center; gap: var(--s-3); min-width: 0; }
      .orders__title { font-size: 1.3rem; font-weight: 600; margin: 0; }

      .station-badge { display: inline-flex; align-items: center; gap: 6px; padding: 4px 10px; border-radius: var(--r-full); font-size: .75rem; border: 1px solid var(--border); color: var(--fg-muted); text-decoration: none; font-family: inherit; white-space: nowrap; }
      .station-badge:hover { border-color: var(--brand-primary); color: var(--brand-primary); }
      .station-badge--counter { border-color: rgba(232,88,12,.4); color: var(--brand-primary); background: rgba(232,88,12,.06); }
      .station-badge--kitchen { border-color: rgba(234,179,8,.4); color: var(--warning); background: rgba(234,179,8,.06); }
      .station-badge--expo    { border-color: rgba(168,85,247,.4); color: var(--brand-accent); background: rgba(168,85,247,.06); }
      .station-badge--all     { border-color: var(--border); color: var(--fg-muted); }
      .station-badge__icon { font-size: .85rem; }

      .rt-badge { display: inline-flex; align-items: center; gap: var(--s-2); padding: 4px 10px; border-radius: var(--r-full); font-size: .75rem; border: 1px solid var(--border); color: var(--fg-muted); }
      .rt-badge .rt-dot { width: 8px; height: 8px; border-radius: var(--r-full); background: var(--fg-dim); }
      .rt-badge--live { color: var(--success); border-color: rgba(34,197,94,.4); background: rgba(34,197,94,.08); }
      .rt-badge--live .rt-dot { background: var(--success); box-shadow: 0 0 6px var(--success); animation: rt-pulse 2s ease-in-out infinite; }
      .rt-badge--offline { color: var(--danger); border-color: rgba(239,68,68,.4); }
      .rt-badge--offline .rt-dot { background: var(--danger); }
      @keyframes rt-pulse { 0%,100% { opacity: 1; } 50% { opacity: .4; } }

      .orders__search { background: var(--bg-surface); border: 1px solid var(--border); border-radius: var(--r-md); color: var(--fg-base); padding: var(--s-2) var(--s-3); font-family: inherit; width: 150px; }
      .orders__refresh { display: flex; align-items: center; gap: var(--s-2); padding: var(--s-2) var(--s-4); background: var(--bg-elevated); border: 1px solid var(--border); color: var(--fg-muted); border-radius: var(--r-md); cursor: pointer; font-size: .85rem; font-family: inherit; white-space: nowrap; flex-shrink: 0; }
      .orders__refresh:hover { border-color: var(--brand-primary); color: var(--brand-primary); }
      .orders__refresh__time { color: var(--fg-dim); font-size: .75rem; font-family: var(--font-mono); }

      .banner { padding: var(--s-3) var(--s-4); border-radius: var(--r-md); }
      .banner.err { background: rgba(239,68,68,.1); color: var(--danger); }
      .banner.loading { background: rgba(59,130,246,.1); color: var(--fg-muted); }

      .order-card { display: grid; grid-template-columns: 1fr 2fr auto; gap: var(--s-5); padding: var(--s-5); background: var(--bg-elevated); border-radius: var(--r-lg); border-left: 4px solid var(--border-strong); }
      .order-card--new    { border-left-color: var(--danger); background: linear-gradient(90deg, rgba(239,68,68,.08), var(--bg-elevated) 20%); }
      .order-card--paid   { border-left-color: var(--success); background: linear-gradient(90deg, rgba(34,197,94,.06), var(--bg-elevated) 20%); }
      .order-card--prep   { border-left-color: var(--warning); }
      .order-card--ready  { border-left-color: var(--brand-accent); background: linear-gradient(90deg, rgba(168,85,247,.06), var(--bg-elevated) 20%); }
      .order-card--done   { border-left-color: var(--fg-dim); opacity: .75; }
      .order-card--cancel { border-left-color: var(--fg-muted); opacity: .55; }
      .order-card--flash { animation: card-flash 1.2s ease-out 2; box-shadow: 0 0 0 2px var(--danger), 0 0 24px rgba(239,68,68,.35); }
      @keyframes card-flash {
        0%   { box-shadow: 0 0 0 2px var(--danger), 0 0 24px rgba(239,68,68,.5); transform: scale(1); }
        30%  { box-shadow: 0 0 0 3px var(--danger), 0 0 40px rgba(239,68,68,.7); transform: scale(1.015); }
        100% { box-shadow: 0 0 0 2px transparent, 0 0 0 transparent; transform: scale(1); }
      }

      .order-card__meta { display: grid; gap: var(--s-2); }
      .order-card__id { font-family: var(--font-mono); font-size: 1.1rem; color: var(--brand-primary); font-weight: 600; }
      .order-card__table { font-size: 1.1rem; font-weight: 600; }
      .order-card__table strong { color: var(--brand-accent); }
      .order-card__time { color: var(--fg-dim); font-size: .8rem; }
      .order-card__badge { margin-top: var(--s-1); font-size: .7rem; color: var(--brand-accent); }

      .order-card__items { display: grid; gap: var(--s-1); }
      .order-card__item { display: flex; justify-content: space-between; font-size: .95rem; padding: var(--s-1) 0; border-bottom: 1px dashed var(--border); }
      .order-card__item:last-child { border-bottom: 0; }
      .order-card__item__qty { color: var(--fg-muted); font-family: var(--font-mono); }
      .order-card__note { margin-top: var(--s-2); padding: var(--s-2) var(--s-3); background: var(--bg-surface); border-left: 2px solid var(--brand-accent); color: var(--fg-muted); font-size: .85rem; border-radius: 0 var(--r-sm) var(--r-sm) 0; }

      .order-card__right { display: grid; gap: var(--s-3); align-content: space-between; }
      .order-card__total { font-family: var(--font-mono); font-size: 1.3rem; color: var(--brand-primary); font-weight: 700; text-align: right; }
      .order-card__actions { display: flex; flex-wrap: wrap; gap: var(--s-2); justify-content: flex-end; }
      .btn-step { padding: var(--s-2) var(--s-4); height: 40px; border-radius: var(--r-md); border: 1px solid var(--border); background: var(--bg-surface); color: var(--fg-base); cursor: pointer; font-size: .85rem; font-family: inherit; }
      .btn-step:hover { border-color: var(--brand-primary); color: var(--brand-primary); }
      .btn-step--primary { background: linear-gradient(135deg, var(--brand-primary), var(--brand-accent)); color: var(--fg-on-brand); border-color: transparent; font-weight: 600; }
      .btn-step--primary:hover { color: var(--fg-on-brand); opacity: .9; }
      .btn-step--danger { background: transparent; border-color: var(--danger); color: var(--danger); }
      .btn-step--danger:hover { background: var(--danger); color: var(--fg-on-brand); }
      .btn-step--print { white-space: nowrap; }
      .btn-step--print[disabled] { opacity: .6; cursor: default; }

      .empty { text-align: center; padding: var(--s-16); color: var(--fg-dim); }
      .empty__icon { font-size: 3rem; opacity: .3; margin-bottom: var(--s-3); }

      /* 手機：單欄堆疊（放在最後，蓋過上面的桌面基礎規則） */
      @media (max-width: 768px) {
        main { grid-template-columns: minmax(0, 1fr); gap: var(--s-4); padding: var(--s-4); }
        .filters { grid-auto-flow: column; grid-auto-columns: max-content; overflow-x: auto; }
        /* 頭列改直向堆疊：title 一行、徽章/搜尋/重新整理換行排，不再擠壓成直排字 */
        .orders__head { flex-direction: column; align-items: stretch; gap: var(--s-3); }
        .orders__head__right { flex-wrap: wrap; }
        .orders__search { flex: 1 1 140px; width: auto; min-width: 0; }
        /* 卡片改單欄堆疊（1fr 2fr auto 在手機會擠爆） */
        .order-card { grid-template-columns: minmax(0, 1fr); gap: var(--s-3); }
        .order-card__total { text-align: left; }
        .order-card__actions { justify-content: flex-start; }
      }
    `;
  }
}

customElements.define('pos-order-list', PosOrderList);
